import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { supabase } from '../lib/supabaseClient';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import Swal from 'sweetalert2';
import { renderBlocks, MarkdownWithMath } from '../components/renderBlocks';
import ClarifyModalView from './ClarifyModalView';

const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['\\[', '\\]']] }
};

const renderAnswerBlocks = (blocks) =>
  blocks.map((block, idx) => {
    if (block.type === 'text') {
      return (
        <div key={idx} className="mb-2">
          <MarkdownWithMath>{block.value}</MarkdownWithMath>
        </div>
      );
    } else if (block.type === 'latex') {
      return (
        <div key={idx} className="my-2 text-indigo-700 font-medium">
          <MathJax dynamic>{block.value}</MathJax>
        </div>
      );
    } else if (block.type === 'image' && block.value) {
      const width = block.width || 'auto';
      const maxWidth = block.maxWidth || '300px';
      return (
        <div key={idx} className="mt-3">
          <img
            src={block.value}
            alt="Answer visual"
            style={{ width, maxWidth }}
            className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg rounded shadow"
            onError={(e) => (e.target.style.display = 'none')}
          />
        </div>
      );
    }
    return null;
  });

export default function InsertLearningQA() {
  // Existing states
  const [jsonText, setJsonText] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [selectedOutline, setSelectedOutline] = useState(null);
  const [status, setStatus] = useState('');
  const [previewConfirmed, setPreviewConfirmed] = useState(false);
  const [orderOptions, setOrderOptions] = useState([]);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [clarifyModalId, setClarifyModalId] = useState(null);
  const [clarifyModalOpen, setClarifyModalOpen] = useState(false);

  // CHANGED: Level 1 -> Level 0
  const [courses, setCourses] = useState([]); // [{value, label, alias}]
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [level0Options, setLevel0Options] = useState([]); // [{value, label}]
  const [selectedLevel0, setSelectedLevel0] = useState(null);

  // Keep raw outlines and compute options from it
  const [outlinesRaw, setOutlinesRaw] = useState([]);
  // { value, label, moduleCode, youtube_url, course, level0Name }

  const handleClarifyModal = (id) => {
    setClarifyModalId(id);
    setClarifyModalOpen(true);
  };

  useEffect(() => {
    if (selectedOutline?.youtube_url) setYoutubeUrl(selectedOutline.youtube_url);
    else setYoutubeUrl('');
  }, [selectedOutline]);

  // Load courses
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('courses')
        .select('name, alias')
        .order('name', { ascending: true });
      if (error) {
        console.error('Error loading courses:', error);
        setCourses([]);
        return;
      }
      setCourses((data || []).map((c) => ({ value: c.name, label: `${c.name}`, alias: c.alias })));
    })();
  }, []);

  // Load all outlines with their course + level0 (great‑grandparent)
  useEffect(() => {
    const fetchOutlines = async () => {
      const { data, error } = await supabase
        .from('topics')
        .select(`
          id,
          name,
          course,
          parent:parent_id (
            module_code,
            parent:parent_id (
              id,
              name,
              parent:parent_id ( id, name )
            )
          ),
          topic_videos(youtube_url)
        `)
        .eq('level', 3)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching outlines:', error);
        setOutlinesRaw([]);
        return;
      }

      const rows = (data || []).map((o) => {
        // level 3 -> parent (level 2) -> parent (level 1) -> parent (level 0)
        const level0Name = o?.parent?.parent?.parent?.name || '';
        const moduleCode = o?.parent?.module_code || 'Misc';
        const course = o?.course || '';
        const youtube_url = o?.topic_videos?.[0]?.youtube_url || '';
        // suffix shows Course • Level 0
        const label = `${moduleCode} - ${o.name} — ${course} • ${level0Name}`;
        return {
          value: o.id,
          label,
          moduleCode,
          youtube_url,
          course,
          level0Name,
          outlineName: o.name,
        };
      });

      setOutlinesRaw(rows);
    };

    fetchOutlines();
  }, []);

  // Derive Level 0 options whenever course changes
  useEffect(() => {
    if (!selectedCourse) {
      setLevel0Options([]);
      setSelectedLevel0(null);
      return;
    }
    const l0 = Array.from(
      new Set(outlinesRaw.filter(o => o.course === selectedCourse.value).map(o => o.level0Name))
    )
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map(n => ({ value: n, label: n }));
    setLevel0Options(l0);
    if (selectedLevel0 && !l0.find(x => x.value === selectedLevel0.value)) {
      setSelectedLevel0(null);
    }
  }, [selectedCourse, outlinesRaw, selectedLevel0]);

  // Filter outlines according to Course + Level 0
  const outlineOptions = useMemo(() => {
    let options = outlinesRaw;
    if (selectedCourse) options = options.filter(o => o.course === selectedCourse.value);
    if (selectedLevel0) options = options.filter(o => o.level0Name === selectedLevel0.value);
    return options.map(o => ({
      value: o.value,
      label: o.label,
      moduleCode: o.moduleCode,
      youtube_url: o.youtube_url,
    }));
  }, [outlinesRaw, selectedCourse, selectedLevel0]);

  // When you choose an outline, keep the youtube url and module code handy
  const onSelectOutline = (opt) => {
    setSelectedOutline(opt);
    setYoutubeUrl(opt?.youtube_url || '');
  };

  // Load order indexes when outline changes
  useEffect(() => {
    const fetchOrderIndexes = async () => {
      if (!selectedOutline) {
        setOrderOptions([]);
        return;
      }
      const { data, error } = await supabase
        .from('qa_learning')
        .select('id,order_index')
        .eq('outline_id', selectedOutline.value)
        .order('order_index');
      if (!error && data) {
        const formatted = data.map((d) => ({
          value: d.id,
          order_index: d.order_index,
          label: `Order ${d.order_index} (ID: ${d.id})`,
          id: d.id
        }));
        setOrderOptions(formatted);
      } else {
        setOrderOptions([]);
      }
    };
    fetchOrderIndexes();
  }, [selectedOutline]);

  const handleYoutubeUpload = async () => {
    if (!selectedOutline) {
      setStatus('❌ Select an outline first.');
      return;
    }

    const { error } = await supabase
      .from('topic_videos')
      .upsert(
        { topic_id: selectedOutline.value, youtube_url: youtubeUrl.trim() },
        { onConflict: ['topic_id'] }
      );

    if (error) setStatus('❌ Failed to save YouTube URL.');
    else setStatus(youtubeUrl.trim() ? '✅ YouTube URL updated successfully.' : '🧹 YouTube URL cleared.');
  };

  const handleParse = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
      const valid = parsed.every(item => (item.answer || item.answer_blocks));
      if (!valid) throw new Error('Each item must have "answer" or "answer_blocks"');
      setParsedItems(parsed);
      setStatus(`✅ Validated ${parsed.length} items.`);
      setPreviewConfirmed(false);
    } catch (err) {
      setParsedItems([]);
      setStatus('❌ Invalid JSON format or missing fields.');
      setPreviewConfirmed(false);
    }
  };

  const handleConfirmPreview = () => {
    if (!selectedOutline || parsedItems.length === 0) {
      setStatus('❌ Select an outline and paste valid JSON.');
      return;
    }
    setPreviewConfirmed(true);
    setStatus('✅ Preview confirmed. You can now insert.');
  };

  const handleFetch = async () => {
    if (!selectedOutline || selectedOrderIndex.length === 0) {
      setStatus('❌ Select outline and at least one order index.');
      return;
    }
    const ids = selectedOrderIndex.map((o) => o.value);
    const { data, error } = await supabase.from('qa_learning').select('*').in('id', ids);
    if (error || !data || data.length === 0) {
      setStatus('❌ No records found.');
      return;
    }
    setJsonText(JSON.stringify(data, null, 2));
    setParsedItems(data);
    setStatus(`✅ Loaded ${data.length} item(s).`);
    setPreviewConfirmed(true);
  };

  const handleDelete = async () => {
    if (!selectedOutline || selectedOrderIndex.length === 0) {
      setStatus('❌ Select outline and at least one order index to delete.');
      return;
    }
    const result = await Swal.fire({
      title: 'Confirm Deletion',
      text: `Delete ${selectedOrderIndex.length} selected item(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;

    let deletedCount = 0;
    for (const order of selectedOrderIndex) {
      const { error } = await supabase.from('qa_learning').delete().eq('id', order.value);
      if (!error) deletedCount++;
    }

    await Swal.fire({ icon: 'success', title: 'Deleted!', text: `Deleted ${deletedCount} item(s).` });
    setStatus(`✅ Deleted ${deletedCount} item(s).`);
    setSelectedOrderIndex([]);
    const { data, error } = await supabase
      .from('qa_learning')
      .select('id, order_index')
      .eq('outline_id', selectedOutline.value)
      .order('order_index');
    if (!error && data) {
      const formatted = data.map((d) => ({
        value: d.id,
        label: `Order ${d.order_index} (ID: ${d.id})`,
        order_index: d.order_index,
        id: d.id
      }));
      setOrderOptions(formatted);
    }
  };

  const handleUpdate = async () => {
    if (!selectedOutline || parsedItems.length === 0) {
      setStatus('❌ Select outline and provide valid JSON.');
      return;
    }
    for (const item of parsedItems) {
      if (!item.id) {
        setStatus('❌ Missing ID in some items. Cannot update.');
        return;
      }
      const { error } = await supabase.from('qa_learning').delete().eq('id', item.id);
      if (error) {
        console.error(`Failed to delete ID ${item.id}:`, error);
        setStatus(`❌ Failed to delete existing row with ID ${item.id}.`);
        return;
      }
    }
    await handleInsert();
  };

  const handleInsert = async () => {
    if (!selectedOutline) {
      setStatus('❌ Select an outline before inserting.');
      return;
    }
    const { data: existingItems, error: fetchError } = await supabase
      .from('qa_learning')
      .select('order_index')
      .eq('outline_id', selectedOutline.value)
      .order('order_index', { ascending: true });

    if (fetchError) {
      console.error('Failed to fetch existing order indexes:', fetchError);
      setStatus('❌ Failed to determine existing order indexes.');
      return;
    }

    let nextOrderIndex =
      existingItems?.length > 0
        ? (existingItems[existingItems.length - 1].order_index ?? 0) + 1
        : 1;

    let successCount = 0;
    for (const item of parsedItems) {
      if (item.order_index == null) item.order_index = nextOrderIndex++;

      if (item.id) {
        const { data: existing, error: existingError } = await supabase
          .from('qa_learning')
          .select('id')
          .eq('id', item.id);

        if (!existingError && existing.length > 0) {
          const result = await Swal.fire({
            title: 'Duplicate ID Found',
            text: `An entry already exists with ID ${item.id}. Do you want to overwrite it?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Overwrite',
            cancelButtonText: 'Cancel'
          });

          if (!result.isConfirmed) {
            setStatus(`⚠️ Skipped ID ${item.id}`);
            continue;
          }
          const { error: delError } = await supabase.from('qa_learning').delete().eq('id', item.id);
          if (delError) {
            console.error(`Delete error for ID ${item.id}:`, delError);
            setStatus(`❌ Failed to overwrite item with ID ${item.id}`);
            continue;
          }
        }
      }

      const { error } = await supabase
        .from('qa_learning')
        .insert([{ ...item, outline_id: selectedOutline.value }]);

      if (error) {
        console.error('Insert error:', error);
        setStatus('❌ Error inserting items.');
        return;
      } else {
        successCount++;
      }
    }

    if (successCount > 0) {
      await Swal.fire({ icon: 'success', title: 'Success!', text: `Inserted ${successCount} item(s) successfully.` });
      setStatus(`✅ Successfully inserted ${successCount} item(s)!`);
      setJsonText('');
      setParsedItems([]);
      setPreviewConfirmed(false);

      const { data, error } = await supabase
        .from('qa_learning')
        .select('id, order_index')
        .eq('outline_id', selectedOutline.value)
        .order('order_index');
      if (!error && data) {
        const formatted = data.map((d) => ({
          value: d.id,
          label: `Order ${d.order_index} (ID: ${d.id})`,
          order_index: d.order_index,
          id: d.id
        }));
        setOrderOptions(formatted);
      }
    } else {
      await Swal.fire({ icon: 'info', title: 'No Items Inserted', text: 'All items may have been skipped or canceled.' });
    }
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-6 text-center text-indigo-700">🪧 Insert Learning Q&A</h2>

        {/* CHANGED: Course + Level 0 filters */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-medium text-gray-700 mb-2">Course</label>
            <Select
              options={courses}
              value={selectedCourse}
              onChange={(opt) => {
                setSelectedCourse(opt);
                setSelectedLevel0(null);
                setSelectedOutline(null);
              }}
              placeholder="Select a course..."
              className="min-w-[240px]"
            />
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-2">Level 0 (Subject)</label>
            <Select
              options={level0Options}
              value={selectedLevel0}
              onChange={(opt) => {
                setSelectedLevel0(opt);
                setSelectedOutline(null);
              }}
              placeholder={selectedCourse ? "Select a Level 0..." : "Select a course first"}
              isDisabled={!selectedCourse}
              className="min-w-[240px]"
            />
          </div>
        </div>

        {/* Outline picker with suffix */}
        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-2">
            Select Outline (suffix includes Course • Level 0)
          </label>
          <Select
            options={outlineOptions}
            value={selectedOutline}
            onChange={onSelectOutline}
            placeholder={selectedCourse ? "Search and select an outline..." : "Select a course first"}
            isDisabled={!selectedCourse}
          />
        </div>

        {/* YouTube URL */}
        {selectedOutline?.youtube_url && (
          <div className="mb-6">
            <div className="aspect-w-16 aspect-h-9 w-full max-w-4xl mx-auto">
              <iframe
                className="w-full h-full rounded shadow"
                src={youtubeUrl}
                title="YouTube Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}
        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-2">YouTube Video or Playlist URL:</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2"
            placeholder="https://www.youtube.com/embed/..."
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            disabled={!selectedOutline}
          />
          <button
            onClick={handleYoutubeUpload}
            className="mt-2 px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            disabled={!selectedOutline}
          >
            Save YouTube URL
          </button>
        </div>

        {/* Fetch / Replace / Delete */}
        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-2">Fetch, Replace or Delete Q&A:</label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Select
              isMulti
              placeholder="Select Order Index(es)"
              options={orderOptions}
              value={selectedOrderIndex}
              onChange={setSelectedOrderIndex}
              className="sm:w-64"
              isDisabled={!selectedOutline}
            />
            <button onClick={handleFetch} className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50" disabled={!selectedOutline}>Fetch</button>
            <button onClick={handleUpdate} className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50" disabled={!selectedOutline}>Replace</button>
            <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50" disabled={!selectedOutline}>Delete</button>
          </div>
        </div>

        {/* JSON area */}
        <div className="mb-4">
          <label className="block font-medium text-gray-700 mb-1">Paste JSON Array:</label>
          <textarea
            rows="10"
            className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='[ { "question": "...", "answer": "...", "hint": "...", "image_url": "...", "order_index": 1 } ]'
          />
          <div className="mt-2 flex gap-4">
            <button onClick={handleParse} className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">Validate JSON</button>
            {parsedItems.length > 0 && (
              <button onClick={handleConfirmPreview} className="px-4 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600">Confirm Preview</button>
            )}
          </div>
        </div>

        {status && <div className="mb-4 text-sm font-medium text-indigo-700">{status}</div>}

        {previewConfirmed && (
          <button
            onClick={handleInsert}
            className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition mb-6"
          >
            🚀 Insert {parsedItems.length} Q&A Items
          </button>
        )}

        {parsedItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">🔍 Preview</h3>
            <ul className="space-y-6">
              {parsedItems.map((item, i) => (
                <li key={i} className="border border-gray-200 rounded p-4 bg-white">
                  <div className="mb-2 font-semibold text-gray-800">
                    <strong>Q{i + 1}:</strong>{' '}
                    {Array.isArray(item.question_blocks)
                      ? renderBlocks(item.question_blocks, handleClarifyModal)
                      : <MarkdownWithMath>{item.question}</MarkdownWithMath>}
                  </div>
                  <div className="text-gray-700 text-sm mb-2">
                    <strong>Answer:</strong>
                    <div className="mt-1">
                      {Array.isArray(item.answer_blocks)
                        ? renderBlocks(item.answer_blocks, handleClarifyModal)
                        : <MarkdownWithMath>{item.answer}</MarkdownWithMath>
                      }
                    </div>
                  </div>
                  {item.hint && (
                    <div className="text-sm text-gray-500 mb-2">
                      <strong>Hint:</strong> <MathJax dynamic>{item.hint}</MathJax>
                    </div>
                  )}
                  {item.image_url && item.image_url.trim() !== '' && (
                    <div className="mt-2">
                      <img src={item.image_url} alt="Visual" className="max-w-sm" style={{ border: 'none', boxShadow: 'none' }} />
                    </div>
                  )}
                  {item.order_index != null && (
                    <div className="text-xs text-gray-400 mt-2">Order Index: {item.order_index}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ClarifyModalView
        clarifyId={clarifyModalId}
        isOpen={clarifyModalOpen}
        onClose={() => setClarifyModalOpen(false)}
      />
    </MathJaxContext>
  );
}
